import { useEffect, useState } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';

import PinImage from '../../assets/images/image-pin.webp';
import {
  Icon,
  LineChart,
  LoadingOverlay,
  NoDataInfo,
  Select,
  TooltipInfo,
  TooltipReferencePrice,
  Button
} from '../../components';
import {
  HomeTypeEnum,
  GraphType,
  IconSize,
  Icons,
  TooltipColor,
  ButtonSize,
  ButtonColor
} from '../../enums';
import strings from '../../language/Swedish';
import { useMapContext } from '../../context/MapContext';
import useBestIndex from '../../api/queries/useBestIndex';
import { useFilterContext } from '../../context/FilterContext';
import { useProjectContext } from '../../context/ProjectContext';
import useIndexPolygon from '../../api/queries/useIndexPolygons';
import { IndexResponseType } from '@/types/apiTypes';
import useIndex from '@/api/queries/useIndex';
import { getTodayDateISO, subtractYearsFromDate, convertDateToISO, filterLineChartDataBasedOnDateInterval, convertDateStringToISO } from '@/helpers';
const StyledPriceDevelopment = styled.div<{ $isMapVisible?: boolean }>`
  ${tw`relative flex flex-col h-full p-4`}
  ${({ $isMapVisible }) => {
    // Apply padding only when map is hidden (to prevent overlap with tabs)
    if (!$isMapVisible) {
      return tw`pt-16 sm:pt-20`;
    }
    // No padding when map is visible
    return '';
  }};
`;

const StickyHeader = styled.div`
  ${tw`sticky top-0 bg-transparent z-10 pb-4 mb-4`}
`;

const TopContainer = styled.div`
  ${tw`flex flex-wrap items-center gap-2 mb-4 text-xs`}
`;

const HeaderContainer = styled.div`
  ${tw`flex flex-1 flex-wrap justify-between items-center mb-3 ml-1 md:w-auto`}
`;

const HeaderLeftContainer = styled.div`
  ${tw`flex items-center gap-1`}
`;

const Heading = styled.h1`
  ${tw`text-base font-normal whitespace-normal mb-0 mr-1`}
`;

const ScrollableContent = styled.div`
  ${tw`flex-1 overflow-y-auto`}
`;

const ChartContainer = styled.div<{ $isLoading: boolean }>`
  ${tw`relative w-full max-w-[1920px] mx-auto`}

  ${({ $isLoading }) =>
    $isLoading ? tw`pointer-events-none` : tw`pointer-events-auto`}
`;

const OptionsContainer = styled.div`
  ${tw`flex items-center gap-2 flex-wrap`}
`;

const StyledInput = styled.input`
  ${tw`flex-none bg-white/80 border border-slate-300 text-xs rounded-lg p-1.5 leading-none text-slate-800 shadow-slate-300 outline-none`};

  &:focus {
    ${tw`bg-white shadow-[0_0_0_4px_rgb(var(--color-primary)_/_0.2)] border-primary`};
  }
`;

const indexTypeOptions = [
  {
    value: HomeTypeEnum.apartment,
    label: strings.priceDevelopment.homeTypeApartments,
  },
  { value: HomeTypeEnum.house, label: strings.priceDevelopment.homeTypeHouses },
];

const graphIndexTypeOptions = [
  { value: GraphType.index, label: strings.priceDevelopment.graphIndex },
  { value: GraphType.fromMap, label: strings.priceDevelopment.graphSold },
  { value: GraphType.fromSqm, label: strings.priceDevelopment.graphSoldSqm },
];

const PriceDevelopment = () => {

  const {
    soldData: {
      refData: { living_area_reference },
    },
  } = useFilterContext();

  const {
    project: { referencePrice, indexValues, index_normalized_fields },
    setProject,
  } = useProjectContext();

  const [selectedGraph, setSelectedGraph] = useState<GraphType>(
    GraphType.index
  );

  const [internalIndexValues, setProjectIndexValues] =
    useState<IndexResponseType | null>({
      index_id: indexValues?.index_id || "",
      reference_date: null,
      reference_value: null,
      reference_type: selectedGraph,
    });

  const { currentCoordinates, setIndexPolygon, isMapVisible } = useMapContext();


  const {
    data: indexData,
    isPending: indexNormalizedLoading,
    isError,
  } = useIndex({ ...internalIndexValues, home_type: (indexValues?.home_type || HomeTypeEnum.apartment) } as any);

  const { data: indexId, isLoading: indexBestLoading } = useBestIndex(
    indexValues?.home_type || HomeTypeEnum.apartment,
    currentCoordinates
  );

  const { data: indexPolygonData } = useIndexPolygon(indexId?.indexId);

  useEffect(() => {
    if (indexPolygonData !== undefined && indexPolygonData) {
      setIndexPolygon(indexPolygonData);
    }
  }, [indexPolygonData, setIndexPolygon]);

  useEffect(() => {
    if (indexId && indexId !== null) {
      setProjectIndexValues({
        index_id: indexId.indexId,
        reference_date: null,
        reference_value: null,
        reference_type: null,
      });
    }
  }, [indexId]);

  useEffect(() => {
    if (indexData !== undefined) {
      setProject((prevState) => ({
        ...prevState,
        indexData: indexData,
      }));
    }
  }, [indexData]);

  useEffect(() => {
    if (indexId?.indexId) {
      const livingArea = living_area_reference || 78;

      // The DB is not updated daily in dev so we use a 1 year old date for reference.
      const referenceDate = new Date().toISOString();
      const refPriceSqm =
        referencePrice.sqmPriceSelected || referencePrice.sqmPrice || 1;
      const refPrice = refPriceSqm * livingArea || refPriceSqm * livingArea;
      switch (selectedGraph) {
        case GraphType.index:
          setProjectIndexValues({
            index_id: indexId.indexId,
            reference_date: null,
            reference_value: null,
            reference_type: selectedGraph,
          });

          setProject((prevState) => ({
            ...prevState,
            indexValues: {
              index_id: indexId.indexId,
              reference_value: null,
              reference_date: null,
              reference_type: selectedGraph,
              home_type: indexValues?.home_type || HomeTypeEnum.apartment
            },
          }));

          break;

        case GraphType.fromMap:
          if (referencePrice) {
            setProjectIndexValues({
              index_id: indexId.indexId,
              reference_date: referenceDate,
              reference_value: refPrice || null,
              reference_type: selectedGraph,
            });
            setProject((prevState) => ({
              ...prevState,
              indexValues: {
                index_id: indexId.indexId,
                reference_value: refPrice || null,
                reference_date: referenceDate,
                reference_type: selectedGraph,
                home_type: indexValues?.home_type || HomeTypeEnum.apartment
              },
            }));
          }

          break;

        case GraphType.fromSqm:
          setProjectIndexValues({
            index_id: indexId.indexId,
            reference_date: referenceDate,
            reference_value: refPriceSqm || null,
            reference_type: selectedGraph,
          });
          setProject((prevState) => ({
            ...prevState,
            indexValues: {
              index_id: indexId.indexId,
              reference_value: refPriceSqm || null,
              reference_date: referenceDate,
              reference_type: selectedGraph,
              home_type: indexValues?.home_type || HomeTypeEnum.apartment
            },
          }));

          break;
      }
    }
  }, [indexId, selectedGraph]);

  function reset() {
    setProject((prevState) => ({
      ...prevState,
      index_normalized_fields: {
        reference_date_from: convertDateToISO(subtractYearsFromDate(new Date(), 15)),
        reference_date_to: getTodayDateISO()
      }
    }));
  }


  function updatePriceDevelopmentSelectedFrom(value: string) {
    setProject((prevState) => ({
      ...prevState,
      index_normalized_fields: {
        reference_date_from: value,
        reference_date_to: index_normalized_fields?.reference_date_to
      }
    }));
  }

  function updatePriceDevelopmentSelectedTo(value: string) {
    setProject((prevState) => ({
      ...prevState,
      index_normalized_fields: {
        reference_date_from: index_normalized_fields?.reference_date_from,
        reference_date_to: value,

      }
    }));
  }

  return (
    <StyledPriceDevelopment $isMapVisible={isMapVisible}>
      {(indexBestLoading || indexNormalizedLoading) && <LoadingOverlay />}
      {indexData ? (
        <>
          <StickyHeader>
            <HeaderContainer>
              <HeaderLeftContainer>
                <Heading>
                  {strings.priceDevelopment.pageHeading} {indexData.short_name}
                </Heading>
                <TooltipInfo
                  content={`${indexData.full_description} ${strings.priceDevelopment.obs}`}>
                  <div>
                    <Icon icon={Icons.info} size={IconSize.small} />
                  </div>
                </TooltipInfo>
              </HeaderLeftContainer>
              {referencePrice?.sqmPrice && (
                <TooltipReferencePrice
                  color={TooltipColor.secondary}
                  referencePrice={
                    referencePrice.sqmPrice * (living_area_reference || 1)
                  }
                  referencePriceSqm={referencePrice.sqmPrice}
                />
              )}
            </HeaderContainer>
            <TopContainer>
              <OptionsContainer>
                <Select
                  id="indexType"
                  options={indexTypeOptions}
                  selectedValue={indexValues?.home_type || HomeTypeEnum.apartment}
                  handleChange={(value) => {
                    setProject((prevState) => ({
                      ...prevState,
                      indexValues: {
                        ...prevState.indexValues,
                        home_type: value
                      }
                    }))
                  }}
                />
                <Select
                  id="grafIndexType"
                  options={graphIndexTypeOptions}
                  selectedValue={selectedGraph}
                  handleChange={(value) => setSelectedGraph(value as GraphType)}
                />
                {strings.filter.from} <StyledInput type="date" value={convertDateStringToISO(index_normalized_fields?.reference_date_from)} onChange={(e) => updatePriceDevelopmentSelectedFrom(e.target.value)} />
                {strings.filter.to} <StyledInput type="date" value={convertDateStringToISO(index_normalized_fields?.reference_date_to)} onChange={(e) => updatePriceDevelopmentSelectedTo(e.target.value)} />
                <Button
                  icon={Icons.refresh}
                  color={ButtonColor.transparent}
                  size={ButtonSize.small}
                  onClick={reset}
                >
                  {strings.data.reset}
                </Button>
              </OptionsContainer>
            </TopContainer>
          </StickyHeader>
          <ScrollableContent>
            <ChartContainer $isLoading={indexBestLoading || indexNormalizedLoading}>
              <LineChart data={filterLineChartDataBasedOnDateInterval(indexData.values, index_normalized_fields?.reference_date_from, index_normalized_fields?.reference_date_to)} />
            </ChartContainer>
          </ScrollableContent>
        </>
      ) : (
        <>
          {!indexBestLoading && !indexNormalizedLoading && !isError && (
            <NoDataInfo
              heading={strings.priceDevelopment.noDataHeading}
              text={strings.priceDevelopment.noDataText}
              img={PinImage}
              altText="pin image"
            />
          )}
          {isError && (
            <NoDataInfo
              heading={strings.data.somethingWentWrong}
              text={strings.priceDevelopment.noDataText}
              img={PinImage}
              altText="pin image"
            />
          )}
        </>
      )}
    </StyledPriceDevelopment>
  );
};

export default PriceDevelopment;
